import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Calendar, AlertCircle, ArrowUp, Minus, ArrowDown } from 'lucide-react'
import type { Task, TaskPriority } from '../../store/projectStore'

interface TaskCardProps {
  task: Task
  onOpen?: (task: Task) => void
}

const PRIORITY_BADGE: Record<TaskPriority, { label: string; icon: React.ElementType; className: string }> = {
  urgent: { label: 'Urgent', icon: AlertCircle, className: 'text-red-600 bg-red-50' },
  high:   { label: 'High',   icon: ArrowUp,     className: 'text-orange-600 bg-orange-50' },
  medium: { label: 'Med',    icon: Minus,       className: 'text-gray-600 bg-gray-50' },
  low:    { label: 'Low',    icon: ArrowDown,   className: 'text-blue-600 bg-blue-50' },
}

function isOverdue(date?: string | null) {
  if (!date) return false
  return new Date(date) < new Date(new Date().toDateString())
}

export default function TaskCard({ task, onOpen }: TaskCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const prio = PRIORITY_BADGE[task.priority]
  const PrioIcon = prio.icon
  const overdue = isOverdue(task.due_date) && task.status !== 'done'

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onOpen?.(task)}
      className="cursor-grab active:cursor-grabbing rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow space-y-2 touch-none"
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-900 line-clamp-2">{task.title}</h4>
        <span className={`inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${prio.className}`}>
          <PrioIcon className="h-3 w-3" />
          {prio.label}
        </span>
      </div>

      {task.completion_percent > 0 && (
        <div className="space-y-1">
          <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-primary-500"
              style={{ width: `${Math.min(100, task.completion_percent)}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-500">{task.completion_percent}%</p>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500">
        {task.due_date ? (
          <div className={`inline-flex items-center gap-1 ${overdue ? 'text-red-600' : ''}`}>
            <Calendar className="h-3 w-3" />
            {new Date(task.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </div>
        ) : <span />}
        {task.assignee_name && (
          <div className="inline-flex items-center gap-1">
            <span className="h-5 w-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-medium flex items-center justify-center">
              {task.assignee_name.charAt(0).toUpperCase()}
            </span>
            <span className="truncate max-w-[80px]">{task.assignee_name}</span>
          </div>
        )}
      </div>
    </div>
  )
}
